/*
 * ******************************************************************
 * Copyright (c) 2021-2026 Broadcom. All Rights Reserved.
 * Broadcom Confidential. The term "Broadcom" refers to Broadcom Inc.
 * and/or its subsidiaries.
 * ******************************************************************
 */
import { Injectable } from "@angular/core";
import { GridState } from "@app/services/app-state/grid-state.model";
import { Router } from "@angular/router";

// @formatter:off
@Injectable({
    providedIn: "root"
})
// @formatter:on
export class AppStateService {
    public isError404: boolean;

    private returnRouteStack: Array<string | string[]> = new Array<string | string[]>;

    private gridToStateMap: Map<GridStateStorageKey, GridState> = new Map<GridStateStorageKey, GridState>();

    constructor(private router: Router) {
    }

    public saveGridState(key: GridStateStorageKey, state: GridState): void {
        this.gridToStateMap.set(key, state);
    }

    public clearGridState(key: GridStateStorageKey = null): void {
        if (key) {
            this.gridToStateMap.set(key, null);
        } else {
            this.gridToStateMap = new Map<GridStateStorageKey, GridState>();
        }
    }

    public getGridState(key: GridStateStorageKey): GridState {
        return this.gridToStateMap.get(key);
    }

    public saveReturnRoute(route?: string): void {
        if (!route) {
            route = this.router.url;
        }
        route = route.split("?")[0];
        this.returnRouteStack.push(route);
    }

    public getReturnRoute(): string | string[] {
        if (this.returnRouteStack.length === 0) {
            return null;
        }
        return this.returnRouteStack.pop();
    }

    public hasReturnRoute(): boolean {
        return this.returnRouteStack.length > 0;
    }

    public resetReturnRoutes(): void {
        this.returnRouteStack = [];
    }
}

export enum GridStateStorageKey {
    ORGANIZATIONS_GRID = "ORGANIZATIONS_GRID",
    ORG_DEPLOYMENTS_GRID = "ORG_DEPLOYMENTS_GRID",
    DEPLOYMENTS_GRID = "DEPLOYMENTS_GRID",
    COMPONENTS_GRID = "COMPONENTS_GRID",
    SUBSCRIPTIONS_GRID = "SUBSCRIPTIONS_GRID",
    REQUESTS_GRID = "REQUESTS_GRID",
    RESOURCES_GRID = "RESOURCES_GRID",
    OAUTH_APPS_GRID = "OAUTH_APPS_GRID",
    RWR_DEPLOYMENTS_GRID = "RWR_DEPLOYMENTS_GRID",
    ORG_RWR_DEPLOYMENTS_GRID = "ORG_RWR_DEPLOYMENTS_GRID",
    CLOUD_COST_MANAGEMENT = "CLOUD_COST_MANAGEMENT",
    CCM_LEAKED_RESOURCES = "CCM_LEAKED_RESOURCES",
    CCM_ACCOUNT_SERVICES = "CCM_ACCOUNT_SERVICES",
    TASKS_GRID = "TASKS_GRID",
    EVENTS_GRID = "EVENTS_GRID",
    ROLLOUT_PLANS_GRID = "ROLLOUT_PLANS_GRID",
    ROLLOUT_WAVES_GRID = "ROLLOUT_WAVES_GRID",
    OFFLINE_LICENSES_GRID = "OFFLINE_LICENSES_GRID",
    ENFORCEMENTS_GRID = "ENFORCEMENTS_GRID",
    SCHEDULED_JOBS_GRID = "SCHEDULED_JOBS_GRID",
    SCHEDULED_JOBS_HISTORY_GRID = "SCHEDULED_JOBS_HISTORY_GRID",
    ROLLOUT_NOTIFICATIONS_GRID = "rollout-notifications",
    WAVE_TARGETS_GRID = "wave-target-grid",
    WAVE_ORG_TARGETS_GRID = "wave-org-target-grid",
    APPROVAL_LOG_GRID = "APPROVAL_LOG_GRID",
    SCRIPT_RUNNER_GRID = "SCRIPT_RUNNER_GRID",
    SCRIPT_EXECUTIONS_GRID = "SCRIPT_EXECUTIONS_GRID",
    SCRIPT_TARGETS_GRID = "SCRIPT_TARGETS_GRID"
}
